/**
 * One-tap fix for a faded print: stretch each colour channel to its full
 * range (which also takes out most of the yellow or magenta cast old prints
 * pick up), lift saturation a little, and sharpen lightly. Returns a new
 * image; the input is left alone so the user can compare and undo.
 */
export function enhancePrint(src: ImageData): ImageData {
  const { width: w, height: h, data: s } = src;
  const n = w * h;

  const hist = [new Uint32Array(256), new Uint32Array(256), new Uint32Array(256)];
  const lumHist = new Uint32Array(256);
  // Every 4th pixel is plenty for percentiles and quarters the cost.
  let sampled = 0;
  for (let p = 0; p < n; p += 4) {
    const i = p * 4;
    const r = s[i]!, g = s[i + 1]!, b = s[i + 2]!;
    hist[0]![r]!++;
    hist[1]![g]!++;
    hist[2]![b]!++;
    lumHist[(r * 77 + g * 150 + b * 29) >> 8]!++;
    sampled++;
  }
  const pct = (hst: Uint32Array, q: number) => {
    const target = sampled * q;
    let acc = 0;
    for (let i = 0; i < 256; i++) {
      acc += hst[i]!;
      if (acc >= target) return i;
    }
    return 255;
  };
  const lumLo = pct(lumHist, 0.005);
  const lumHi = pct(lumHist, 0.995);

  // Per-channel levels, blended with the overall levels so a print that
  // really is mostly one colour (a red dress filling the frame) isn't
  // "corrected" into grey.
  const lut = [0, 1, 2].map((c) => {
    const lo = Math.round(pct(hist[c]!, 0.005) * 0.65 + lumLo * 0.35);
    const hi = Math.round(pct(hist[c]!, 0.995) * 0.65 + lumHi * 0.35);
    // At most a 2.5× stretch: beyond that a print is mostly blank paper or
    // shadow, and stretching further only amplifies grain and JPEG noise.
    // When capped, widen around the middle so the tones stay centred.
    const range = Math.max(102, hi - lo);
    const start = (lo + hi) / 2 - range / 2;
    const table = new Uint8ClampedArray(256);
    for (let i = 0; i < 256; i++) table[i] = ((i - start) * 255) / range;
    return table;
  });

  const SAT = 1.12;
  const leveled = new Uint8ClampedArray(s.length);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    const r = lut[0]![s[i]!]!;
    const g = lut[1]![s[i + 1]!]!;
    const b = lut[2]![s[i + 2]!]!;
    const l = 0.299 * r + 0.587 * g + 0.114 * b;
    leveled[i] = l + (r - l) * SAT;
    leveled[i + 1] = l + (g - l) * SAT;
    leveled[i + 2] = l + (b - l) * SAT;
    leveled[i + 3] = 255;
  }

  // Light sharpen: centre minus a quarter of each 4-neighbour.
  const A = 0.25;
  const out = new ImageData(w, h);
  const o = out.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
        o[i] = leveled[i]!;
        o[i + 1] = leveled[i + 1]!;
        o[i + 2] = leveled[i + 2]!;
      } else {
        const up = i - w * 4;
        const dn = i + w * 4;
        for (let c = 0; c < 3; c++) {
          o[i + c] =
            leveled[i + c]! * (1 + 4 * A) -
            A * (leveled[i - 4 + c]! + leveled[i + 4 + c]! + leveled[up + c]! + leveled[dn + c]!);
        }
      }
      o[i + 3] = 255;
    }
  }
  return out;
}
